import { __awaiter, __decorate, __extends, __generator, __metadata } from "tslib";
import { PageRequest } from './PageRequest';
import NeoModel from '../../Model/NeoModel';
import { DataViewBase } from '../DataView';
import { Misc, AppServices } from '../../';
import { Observable } from '../../Model/Decorators';
import { SortOperation } from '../SortOperation';
import List from '../../Model/List';
var PageManager = /** @class */ (function (_super) {
    __extends(PageManager, _super);
    function PageManager(criteria, modelType, dataSource, options) {
        var _this = _super.call(this, []) || this;
        _this.modelType = modelType;
        _this.dataSource = dataSource;
        _this.hasInitialised = false;
        _this._totalRecords = -1;
        _this.currentData = new List(_this.modelType);
        _this.initialFetchState = "none";
        _this.isInvalidated = false;
        _this.options = options || {};
        _this.allowSort = _this.options.allowSort !== false;
        _this.performsRemoteSorting = true;
        _this.pageRequest = new PageRequest(criteria);
        _this.pageRequest.pageSize = _this.options.pageSize || 20;
        // Create an instance of model type so that we know about its properties.
        void new modelType();
        if (options) {
            if (options.sortBy) {
                _this.setSortOperations(options.sortBy, options.sortAscending);
            }
            _this.taskRunner = options.taskRunner;
        }
        if (!options || !options.taskRunner) {
            _this.taskRunner = Misc.Globals.appService.get(AppServices.NeoTypes.TaskRunner);
        }
        if (!_this.options.pageSizeOptions) {
            _this.options.pageSizeOptions = Misc.Settings.pager.pageSizeOptions;
        }
        return _this;
    }
    Object.defineProperty(PageManager.prototype, "criteria", {
        /**
         * Gets or sets the criteria.
         */
        get: function () {
            return this.pageRequest.criteria;
        },
        set: function (value) {
            this.pageRequest.criteria = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PageManager.prototype, "hasFetched", {
        /**
         * Returns the state of the initial data request. Does not change after the first request has been made.
         * Note, this will return null while loading, so you should check against a boolean value. E.g. if (hasFetched === false)
         */
        get: function () {
            if (this.initialFetchState === "loading") {
                return null;
            }
            void this.isInvalidated;
            return this.initialFetchState === "complete";
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PageManager.prototype, "pageNo", {
        /**
         * Gets or sets the 1 based page number.
         */
        get: function () {
            return this.pageRequest.pageIndex + 1;
        },
        set: function (value) {
            var newIndex = Math.max(1, Math.min(this.totalPages, value)) - 1;
            if (this.pageRequest.pageIndex !== newIndex) {
                this.pageRequest.pageIndex = newIndex;
                this.refreshData(false);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PageManager.prototype, "pageSize", {
        get: function () {
            return this.pageRequest.pageSize;
        },
        set: function (value) {
            var prevSize = this.pageRequest.pageSize;
            var prevPageNo = this.pageNo;
            if (value !== prevSize) {
                this.pageRequest.pageSize = value;
                // Adjust the page no so we show the same records, this will re-fetch data.
                this.pageNo = Math.floor((this.pageRequest.pageIndex * prevSize + 1) / value) + 1;
                if (this.pageNo === prevPageNo) {
                    this.refreshData(false);
                }
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PageManager.prototype, "totalRecords", {
        get: function () {
            return this._totalRecords;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PageManager.prototype, "totalPages", {
        /** Gets the total number of pages based on the total number of records. */
        get: function () {
            if (this.totalRecords <= 0) {
                return 1;
            }
            else {
                return Math.ceil(this.totalRecords / this.pageRequest.pageSize);
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Called by the pager component on first display. Will fetch data if options.fetchInitial is set to true.
     */
    PageManager.prototype.initialise = function () {
        if (!this.hasInitialised) {
            this.hasInitialised = true;
            this.isInvalidated = false;
            if (this.options.fetchInitial) {
                this.refreshData(true, this.options.initialTaskRunner);
            }
        }
        else {
            this.afterRender();
        }
    };
    /** Called after a bound ui component has rendered. Used to refetch data if invalidateData() was called. */
    PageManager.prototype.afterRender = function () {
        if (this.isInvalidated) {
            this.isInvalidated = false;
            this.refreshPage();
        }
    };
    /**
     * Refresh the data by calling the api endpoint with the current criteria.
     * Note: the page index will be reset to zero. Use this when your criteria has changed
     * @param getCount True if criteria has changed, and the total number of records needs to be refetched.
     * @param taskRunner Optional alternate task runner to use.
     */
    PageManager.prototype.refreshData = function () {
        return __awaiter(this, arguments, void 0, function (getCount, taskRunner) {
            var request;
            if (getCount === void 0) { getCount = true; }
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (getCount) {
                            // Reset the page index when the no of records returned is expected to change (e.g. when filtering).
                            this.pageRequest.pageIndex = 0;
                        }
                        this.pageRequest.noCount = !getCount;
                        this.pageRequest.sortColumns = this.sortInfo;
                        request = this.pageRequest.toQueryObject();
                        if (this.options.beforeFetch)
                            this.options.beforeFetch(request);
                        if (!taskRunner)
                            taskRunner = this.taskRunner;
                        if (this.initialFetchState === "none") {
                            this.initialFetchState = "loading";
                        }
                        return [4 /*yield*/, taskRunner.waitFor(this.fetchAndSetData(request))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Refresh the data only if the page manager has initialised, and is currently being rendered.
     */
    PageManager.prototype.invalidateData = function () {
        if (this.hasInitialised) {
            this.isInvalidated = true;
        }
    };
    /**
     * Refresh the data by calling the api endpoint with the current criteria.
     * This will not reset the page index, and will not update the total record count, therefore should only be called if your criteria has not changed since the last full fetch using refreshData().
     * @param taskRunner Optional alternate task runner to use.
     */
    PageManager.prototype.refreshPage = function (taskRunner) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.hasInitialised) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.refreshData(false, taskRunner)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    PageManager.prototype.fetchAndSetData = function (request) {
        return __awaiter(this, void 0, void 0, function () {
            var response, data;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.dataSource(request)];
                    case 1:
                        response = _b.sent();
                        if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.entityList) {
                            data = response.data;
                        }
                        else {
                            data = response;
                        }
                        return [4 /*yield*/, this.setData(data.entityList, data.total)];
                    case 2:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /* Will set the data to the provided list. This will still invoke the `afterFetch` */
    PageManager.prototype.setData = function (pageRecords_1) {
        return __awaiter(this, arguments, void 0, function (pageRecords, totalRecords) {
            var newData, task;
            if (totalRecords === void 0) { totalRecords = 0; }
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (totalRecords >= 0) {
                            this._totalRecords = totalRecords;
                        }
                        newData = List.fromJSArray(this.modelType, pageRecords, this.options.deserialiseOptions);
                        if (!this.options.afterFetch) return [3 /*break*/, 2];
                        task = this.options.afterFetch(newData);
                        if (!(task !== void 0)) return [3 /*break*/, 2];
                        return [4 /*yield*/, task];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        this.currentData = newData;
                        this.initialFetchState = "complete";
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Adds an item to the current page, and increments the total record count.
     */
    PageManager.prototype.addRecord = function (item) {
        this._totalRecords += 1;
        if (item.meta) {
            this.data.push(item);
        }
        else {
            this.data.push(this.modelType.fromJSObject(item));
        }
    };
    /**
     * Resets the page manager to its initial state and clears the current data.
     */
    PageManager.prototype.reset = function () {
        this.initialFetchState = "none";
        this.data.set([]);
        this._totalRecords = 0;
    };
    /**
     * Resets the sort properties. A new sort property can be provided.
     * If the provided sort property matches the first existing sort property, and sortAscending is undefined, then the current sort order will be reversed.
     */
    PageManager.prototype.resetSort = function (property, sortAscending) {
        _super.prototype.resetSort.call(this, property, sortAscending);
        this.refreshData(false);
    };
    /**
     * Adds a sort property.
     * If the property already exists, the sort direction of the property will be reversed.
     */
    PageManager.prototype.addSort = function (property, sortAscending) {
        _super.prototype.addSort.call(this, property, sortAscending);
        this.refreshData(false);
    };
    /** Sets a single or multiple sort operations. Does not refresh data. */
    PageManager.prototype.setSortOperations = function (sortBy, sortAscending) {
        this.sortInfo = [];
        var metaData = this.modelType.neo.meta;
        if (sortBy instanceof Array) {
            for (var i = 0; i < sortBy.length; i++) {
                this.sortInfo.push(new SortOperation(metaData[sortBy[i]].propertyInfo, sortAscending === undefined || sortAscending[i] !== false));
            }
        }
        else {
            this.sortInfo.push(new SortOperation(metaData[sortBy].propertyInfo, sortAscending !== false));
        }
    };
    Object.defineProperty(PageManager.prototype, "rawItems", {
        get: function () {
            return this.currentData;
        },
        enumerable: false,
        configurable: true
    });
    PageManager.prototype.getItems = function () {
        return this.currentData;
    };
    Object.defineProperty(PageManager.prototype, "data", {
        /**
         * Returns the current list.
         */
        get: function () {
            return this.currentData;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PageManager.prototype, "pageSizeOptions", {
        get: function () {
            return this.options.pageSizeOptions;
        },
        enumerable: false,
        configurable: true
    });
    PageManager.prototype.getMetaItem = function () {
        return new this.modelType();
    };
    PageManager.prototype.firstPage = function () {
        this.pageNo = 1;
    };
    PageManager.prototype.previousPage = function () {
        this.pageNo -= 1;
    };
    PageManager.prototype.nextPage = function () {
        this.pageNo += 1;
    };
    PageManager.prototype.lastPage = function () {
        this.pageNo = this.totalPages;
    };
    __decorate([
        Observable(),
        __metadata("design:type", Number)
    ], PageManager.prototype, "_totalRecords", void 0);
    __decorate([
        Observable(),
        __metadata("design:type", Object)
    ], PageManager.prototype, "currentData", void 0);
    __decorate([
        Observable(),
        __metadata("design:type", String)
    ], PageManager.prototype, "initialFetchState", void 0);
    __decorate([
        Observable(),
        __metadata("design:type", Object)
    ], PageManager.prototype, "isInvalidated", void 0);
    PageManager = __decorate([
        NeoModel,
        __metadata("design:paramtypes", [Object, Function, Function, Object])
    ], PageManager);
    return PageManager;
}(DataViewBase));
export { PageManager };
